import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SectionHeader } from '../components/ui/SectionHeader';
import { RELEASES } from '../constants';
import { getArtistsForClient } from '../services/artistService';
import { Instagram, Music, Play, Globe, ChevronLeft, ArrowRight, FileText, Calendar, MapPin, DollarSign, Send, Check } from 'lucide-react';
import { Artist } from '../types';
import { SEO } from '../components/SEO';
import { sendForm } from '../services/formService';

type ViewMode = 'grid' | 'detail' | 'booking';

interface ArtistsProps {
  initialView?: ViewMode;
}

const initialBookingFormState = {
    promoterName: '',
    email: '',
    artistIds: [] as string[],
    date: '',
    venue: '',
    budget: '',
    details: ''
};

export const Artists: React.FC<ArtistsProps> = ({ initialView = 'grid' }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [artists, setArtists] = useState<Artist[]>([]);

  useEffect(() => {
      const loadArtists = async () => {
          const fetchedArtists = await getArtistsForClient();
          setArtists(fetchedArtists);
      };
      loadArtists();
  }, []);
  
  // This effect synchronizes the internal view mode with the prop from the parent.
  useEffect(() => {
    if (initialView === 'booking') {
      setViewMode('booking');
      setSelectedArtist(null);
    } else if (id && artists.length > 0) {
      const artist = artists.find(a => a.id === id);
      if (artist) {
        setSelectedArtist(artist);
        setViewMode('detail');
      } else {
        // If ID is invalid, go back to grid
        navigate('/artists', { replace: true });
      }
    } else {
      setViewMode('grid');
      setSelectedArtist(null);
    }
  }, [initialView, id, artists, navigate]);

  // Booking Form State
  const [bookingForm, setBookingForm] = useState(initialBookingFormState);
  const [formStatus, setFormStatus] = useState<'idle' | 'success'>('idle');

  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [viewMode, selectedArtist]);

  const openBooking = (artist?: Artist) => {
    setBookingForm(prev => ({
        ...prev,
        artistIds: artist ? [artist.id] : prev.artistIds
    }));
    setViewMode('booking');
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (bookingForm.artistIds.length === 0) {
      alert('Please select at least one artist.');
      return;
    }

    const artistNames = bookingForm.artistIds
      .map(artistId => artists.find(a => a.id === artistId)?.name)
      .filter(Boolean)
      .join(', ');
    
    const dataToSend = { ...bookingForm, selectedArtists: artistNames };
    const { success, error } = await sendForm('booking', dataToSend);

    if (success) {
      setFormStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert("Error submitting form: " + error);
      setFormStatus('idle');
    }
  };

  const handleArtistSelect = (artistId: string) => {
    setBookingForm(prev => {
        const isSelected = prev.artistIds.includes(artistId);
        const newArtistIds = isSelected
            ? prev.artistIds.filter(id => id !== artistId)
            : [...prev.artistIds, artistId];
        return { ...prev, artistIds: newArtistIds };
    });
  };


  // ----------------------------------------------------------------------
  // VIEW: BOOKING FORM
  // ----------------------------------------------------------------------
  if (viewMode === 'booking') {
    return (
        <div className="py-12 animate-in fade-in slide-in-from-bottom-5 duration-500 min-h-screen">
            <SEO 
                title="Book Artists" 
                description="Book AstralVibe artists for your next event. Worldwide representation for Afro House and Techno talent." 
                url="/booking"
                keywords={["book djs", "artist booking", "techno djs", "afro house djs", "event booking"]}
            />
            <div className="container mx-auto px-6 max-w-4xl">
                <button 
                    onClick={() => {
                      if (selectedArtist) {
                        navigate(`/artists/${selectedArtist.id}`);
                      } else {
                        navigate('/artists');
                      }
                    }}
                    className="group flex items-center gap-2 mb-8 text-gray-300 hover:text-white transition-colors"
                >
                    <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="text-xs font-bold uppercase tracking-widest">Back to {selectedArtist ? 'Artist' : 'Roster'}</span>
                </button>

                {formStatus === 'success' ? (
                    <div className="bg-black/80 backdrop-blur-xl border border-green-500/30 p-12 text-center rounded-xl shadow-2xl">
                        <div className="w-20 h-20 bg-green-500/20 border border-green-500/50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="w-10 h-10 text-green-500" />
                        </div>
                        <h2 className="text-3xl font-display font-bold text-white mb-4">Offer Transmitted</h2>
                        <p className="text-slate-200 mb-8 max-w-lg mx-auto">
                            Your booking request has been securely sent.
                            Our booking agents will review the offer details and respond within 24 hours.
                        </p>
                        <button 
                            onClick={() => { 
                                setFormStatus('idle'); 
                                setBookingForm(initialBookingFormState);
                                if (selectedArtist) {
                                  navigate(`/artists/${selectedArtist.id}`);
                                } else {
                                  navigate('/artists');
                                }
                            }}
                            className="px-8 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-astral-purple hover:text-white transition-colors"
                        >
                            Return to Profile
                        </button>
                    </div>
                ) : (
                    <div className="bg-black/70 backdrop-blur-xl border border-white/10 p-8 md:p-12 rounded-xl relative shadow-2xl">
                         <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-astral-purple opacity-20 blur-[50px]"></div>
                         
                         <div className="mb-10 relative z-10">
                            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-2 uppercase">Booking Request</h2>
                            <p className="text-slate-300 font-light">Submit an official offer for our roster talent.</p>
                         </div>

                         <form name="booking" onSubmit={handleBookingSubmit} className="space-y-8 relative z-10">
                            
                            {/* Artist Selection */}
                            <div className="space-y-4">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Select Artist(s)</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {artists.map(artist => {
                                        const isSelected = bookingForm.artistIds.includes(artist.id);
                                        return (
                                            <button
                                                type="button"
                                                key={artist.id}
                                                onClick={() => handleArtistSelect(artist.id)}
                                                className={`relative aspect-[4/5] md:aspect-square border-2 transition-all duration-300 rounded-md overflow-hidden group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-astral-dark focus:ring-astral-purple ${
                                                    isSelected ? 'border-astral-purple shadow-lg shadow-astral-purple/30' : 'border-transparent hover:border-white/50'
                                                }`}
                                            >
                                                <img src={artist.image} alt={artist.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                                                <div className="absolute bottom-0 left-0 p-3 text-left w-full">
                                                    <p className="text-white font-bold text-sm uppercase tracking-wider truncate">{artist.name}</p>
                                                </div>
                                                {isSelected && (
                                                    <div className="absolute top-2 right-2 w-6 h-6 bg-astral-purple rounded-full flex items-center justify-center border-2 border-astral-dark">
                                                        <Check className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                            
                            {/* Event Date */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Event Date</label>
                                <div className="relative">
                                    <input 
                                        required
                                        type="date" 
                                        name="date"
                                        value={bookingForm.date}
                                        onChange={e => setBookingForm({...bookingForm, date: e.target.value})}
                                        className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                        <Calendar className="w-4 h-4" />
                                    </div>
                                </div>
                            </div>

                            {/* Venue & Budget */}
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Venue / Location</label>
                                    <div className="relative">
                                        <input 
                                            required
                                            type="text" 
                                            name="venue"
                                            value={bookingForm.venue}
                                            onChange={e => setBookingForm({...bookingForm, venue: e.target.value})}
                                            className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                            placeholder="Club Name, City, Country"
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                            <MapPin className="w-4 h-4" />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Budget / Offer</label>
                                    <div className="relative">
                                        <input 
                                            required
                                            type="text" 
                                            name="budget"
                                            value={bookingForm.budget}
                                            onChange={e => setBookingForm({...bookingForm, budget: e.target.value})}
                                            className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                            placeholder="Ex: €2000 + Travel + Hotel"
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                            <DollarSign className="w-4 h-4" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                             {/* Contact Info */}
                             <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Promoter Name</label>
                                    <input 
                                        required
                                        type="text" 
                                        name="promoterName"
                                        value={bookingForm.promoterName}
                                        onChange={e => setBookingForm({...bookingForm, promoterName: e.target.value})}
                                        className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                        placeholder="Agency or Contact Person"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Email Address</label>
                                    <input 
                                        required
                                        type="email" 
                                        name="email"
                                        value={bookingForm.email}
                                        onChange={e => setBookingForm({...bookingForm, email: e.target.value})}
                                        className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                        placeholder="you@domain.com"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold uppercase tracking-widest text-astral-cyan">Additional Details</label>
                                <textarea 
                                    rows={4}
                                    name="details"
                                    value={bookingForm.details}
                                    onChange={e => setBookingForm({...bookingForm, details: e.target.value})}
                                    className="w-full bg-black/80 border border-white/20 p-4 text-white focus:border-astral-purple focus:outline-none rounded-sm transition-colors placeholder-slate-400"
                                    placeholder="Set times, lineup details, travel logistics, etc."
                                />
                            </div>

                            <button 
                                type="submit"
                                className="w-full bg-white text-black font-display font-bold uppercase tracking-[0.2em] py-5 hover:bg-astral-purple hover:text-white transition-all flex items-center justify-center gap-2 group shadow-lg hover:shadow-astral-purple/50"
                            >
                                Submit Offer <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                         </form>
                    </div>
                )}
            </div>
        </div>
    );
  }

  // ----------------------------------------------------------------------
  // VIEW: DETAIL
  // ----------------------------------------------------------------------
  if (viewMode === 'detail' && selectedArtist) {
     const releases = RELEASES.filter(r => r.artist.toLowerCase() === selectedArtist.name.toLowerCase());

     const artistStructuredData = {
        "@context": "https://schema.org",
        "@type": "MusicGroup",
        "name": selectedArtist.name,
        "description": selectedArtist.bio,
        "image": selectedArtist.image,
        "genre": selectedArtist.genre,
        "url": `https://astralvibe.art/artists/${selectedArtist.id}`,
        "sameAs": [
            selectedArtist.instagram,
            selectedArtist.spotify,
            selectedArtist.soundcloud
        ].filter(Boolean)
     };

     return (
        <div className="min-h-screen bg-astral-black relative animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SEO 
                title={selectedArtist.name} 
                description={`Listen to ${selectedArtist.name} on AstralVibe. ${selectedArtist.genre} artist. ${selectedArtist.bio.substring(0, 150)}...`} 
                image={selectedArtist.image}
                url={`/artists/${selectedArtist.id}`}
                type="profile"
                keywords={[selectedArtist.name, selectedArtist.genre, "electronic music artist", "techno artist", "afro house artist"]}
                structuredData={artistStructuredData}
            />
            {/* Close / Back Button */}
            <div className="fixed top-24 left-6 md:left-12 z-50">
                <button 
                    onClick={() => navigate('/artists')}
                    className="flex items-center gap-2 px-6 py-3 bg-black/80 backdrop-blur-md border border-white/20 rounded-full hover:bg-white hover:text-black transition-all group"
                >
                    <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-mono text-xs uppercase tracking-widest">Back to Roster</span>
                </button>
            </div>

            {/* Hero Section */}
            <div className="relative h-[70vh] w-full flex flex-col justify-end">
                <div className="absolute inset-0 bg-gradient-to-t from-astral-black via-astral-black/70 to-transparent z-10"></div>
                <div className="absolute inset-0 bg-space-gradient opacity-30 mix-blend-overlay z-10"></div>
                <img 
                    src={selectedArtist.image} 
                    alt={selectedArtist.name}
                    className="absolute inset-0 w-full h-full object-cover object-top filter contrast-110 saturate-0 hover:saturate-50 transition-all duration-[2s]" 
                />
                
                <div className="relative z-20 container mx-auto px-6">
                     <div className="max-w-4xl p-8 md:p-12">
                        <span className="text-astral-cyan font-mono tracking-widest uppercase bg-black/50 backdrop-blur-md px-4 py-2 border border-astral-cyan/30 rounded-sm mb-4 inline-block animate-in slide-in-from-left duration-700">
                            {selectedArtist.genre}
                        </span>
                        <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-display font-black uppercase text-white tracking-tighter mb-4 leading-none animate-in slide-in-from-bottom duration-700 delay-100 text-glow-strong">
                            {selectedArtist.name}
                        </h1>
                     </div>
                </div>
            </div>

            {/* Content Details */}
            <div className="container mx-auto px-6 py-24">
                <div className="grid md:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left Column: Bio & Discography/Spotify */}
                    <div className="md:col-span-8 space-y-20">
                        {/* Bio */}
                        <div>
                            <h3 className="text-2xl font-display font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-4">
                               <span className="w-8 h-[2px] bg-astral-purple"></span> Biography
                            </h3>
                            <div className="prose prose-invert max-w-none space-y-6">
                                <p className="text-xl md:text-2xl font-light text-white leading-relaxed p-6 bg-white/5 border-l-4 border-astral-purple/50">
                                    "{selectedArtist.bio}"
                                </p>
                                <p className="text-slate-200 text-lg leading-loose">
                                    Rising from the underground currents of the electronic music scene, {selectedArtist.name} has established a sonic identity that defies simple categorization. 
                                    With a meticulous approach to sound design and an instinct for the dancefloor's emotional trajectory, they construct sets and productions that serve as portals to other dimensions.
                                </p>
                                <p className="text-slate-400 text-lg leading-loose">
                                    Their work explores the intersection of organic rhythm and digital futurism, creating a unique soundscape that has resonated in clubs from Berlin to Ibiza. 
                                    As a core member of the AstralVibe collective, {selectedArtist.name} continues to push the envelope of what modern electronic music can convey.
                                </p>
                            </div>
                        </div>

                        {/* Music Section: Embed OR List */}
                        {selectedArtist.spotifyId ? (
                            <div>
                                <h3 className="text-2xl font-display font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-4">
                                    <span className="w-8 h-[2px] bg-astral-purple"></span> Latest Streams
                                </h3>
                                <div className="border border-white/10 bg-black/50 rounded-lg overflow-hidden shadow-2xl">
                                  <iframe 
                                    src={`https://open.spotify.com/embed/artist/${selectedArtist.spotifyId}?utm_source=generator&theme=0`} 
                                    width="100%" 
                                    height="450" 
                                    frameBorder="0" 
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                                    loading="lazy"
                                    title={`Spotify Player - ${selectedArtist.name}`}
                                  ></iframe>
                                </div>
                            </div>
                        ) : (
                            releases.length > 0 ? (
                                <div>
                                    <h3 className="text-2xl font-display font-bold text-white uppercase tracking-widest mb-8 flex items-center gap-4">
                                        <span className="w-8 h-[2px] bg-astral-purple"></span> Selected Works
                                    </h3>
                                    <div className="grid gap-4">
                                        {releases.map(release => (
                                            <div key={release.id} onClick={() => navigate(`/releases/${release.id}`)} className="flex items-center gap-4 md:gap-6 p-4 md:p-6 border border-white/5 bg-black/40 hover:bg-black/80 hover:border-astral-purple/50 transition-all group cursor-pointer rounded-sm shadow-lg">
                                                <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0 overflow-hidden rounded-sm">
                                                    <img src={release.cover} alt={release.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                                </div>
                                                <div className="flex-grow">
                                                    <h4 className="font-display font-bold uppercase text-lg md:text-xl text-white group-hover:text-astral-purple transition-colors">{release.title}</h4>
                                                    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs font-mono text-gray-500 mt-2 uppercase tracking-widest">
                                                        <span>{release.type}</span>
                                                        <span className="w-1 h-1 bg-gray-500 rounded-full hidden sm:block"></span>
                                                        <span>{release.catalogNumber}</span>
                                                        <span className="w-1 h-1 bg-gray-500 rounded-full hidden sm:block"></span>
                                                        <span>{release.date.split('-')[0]}</span>
                                                    </div>
                                                </div>
                                                <button className="hidden md:flex items-center gap-2 px-6 py-3 border border-white/20 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-astral-purple hover:border-astral-purple text-white transition-all">
                                                    <Play className="w-3 h-3 fill-current" /> Stream
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                               <div className="p-8 border border-dashed border-white/10 bg-white/5 rounded-sm">
                                    <p className="text-gray-500 font-mono text-sm uppercase tracking-widest text-center">No releases cataloged in the mainframe yet.</p>
                               </div>
                            )
                        )}
                    </div>

                    {/* Right Column: Sticky Sidebar */}
                    <div className="md:col-span-4">
                         <div className="md:sticky top-28 space-y-8">
                             <div className="bg-black/60 backdrop-blur-md border border-white/10 p-8 rounded-lg space-y-8">
                                {/* Intel Section */}
                                <div>
                                     <h4 className="font-display text-xl uppercase tracking-widest text-white mb-6 border-b border-white/10 pb-4">Intel</h4>
                                     <div className="flex flex-col gap-6">
                                        {selectedArtist.instagram && (
                                            <a href={selectedArtist.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-white hover:text-astral-purple transition-colors group">
                                                <span className="flex items-center gap-3"><Instagram className="w-5 h-5" /> Instagram</span>
                                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                            </a>
                                        )}
                                        {selectedArtist.spotify && (
                                            <a href={selectedArtist.spotify} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-white hover:text-[#1DB954] transition-colors group">
                                                <span className="flex items-center gap-3"><Music className="w-5 h-5" /> Spotify</span>
                                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                            </a>
                                        )}
                                        {selectedArtist.soundcloud && (
                                            <a href={selectedArtist.soundcloud} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-white hover:text-[#ff5500] transition-colors group">
                                                <span className="flex items-center gap-3"><Globe className="w-5 h-5" /> SoundCloud</span>
                                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                            </a>
                                        )}
                                        {selectedArtist.epk && (
                                            <a href={selectedArtist.epk} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between text-white hover:text-astral-cyan transition-colors group">
                                                <span className="flex items-center gap-3"><FileText className="w-5 h-5" /> Download EPK</span>
                                                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                                            </a>
                                        )}
                                     </div>
                                </div>
                                
                                {/* Booking Section */}
                                <div>
                                     <h4 className="font-display text-xl uppercase tracking-widest text-white mb-4">Booking</h4>
                                     <p className="text-sm text-slate-300 mb-6 leading-relaxed">
                                         Worldwide representation for club nights, festivals, and special events.
                                     </p>
                                     <button 
                                         onClick={() => openBooking(selectedArtist)}
                                         className="block w-full py-4 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-astral-purple hover:text-white transition-all duration-300"
                                     >
                                         Contact Agent
                                     </button>
                                </div>
                             </div>
                         </div>
                    </div>
                </div>
            </div>
        </div>
     );
  }

  // ----------------------------------------------------------------------
  // VIEW: GRID
  // ----------------------------------------------------------------------
  const rosterStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": artists.map((artist, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
            "@type": "MusicGroup",
            "name": artist.name,
            "image": artist.image,
            "url": `https://astralvibe.art/artists/${artist.id}`
        }
    }))
  };

  return (
    <div className="py-12">
      <SEO 
        title="Artists Roster" 
        description="Meet the architects of the AstralVibe sound. Discover our roster of visionary electronic music artists." 
        url="/artists"
        keywords={["electronic music artists", "techno artists", "afro house artists", "dj roster", "astralvibe artists"]}
        structuredData={rosterStructuredData}
      />
      <div className="container mx-auto px-6">
        <SectionHeader subtitle="The Roster" title="Visionary Talent" />
        
        <div className="grid md:grid-cols-2 gap-8">
          {artists.map((artist) => (
            <div 
              key={artist.id} 
              onClick={() => navigate(`/artists/${artist.id}`)}
              className="group relative h-[450px] md:h-[500px] overflow-hidden bg-gray-900 border border-white/5 cursor-pointer"
            >
              <img 
                src={artist.image} 
                alt={artist.name} 
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-40 group-hover:scale-105 transition-all duration-700 grayscale group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
              
              <div className="absolute bottom-0 left-0 w-full p-8 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-astral-cyan font-mono text-xs tracking-widest uppercase mb-2 block">
                  {artist.genre}
                </span>
                <h3 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold uppercase mb-4 text-white group-hover:text-astral-purple transition-colors">
                  {artist.name}
                </h3>
                <p className="text-slate-300 mb-6 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 line-clamp-2">
                  {artist.bio}
                </p>
                
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-200 text-xs font-bold uppercase tracking-widest text-white">
                  <span>View Profile</span> <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};